import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';
import {
  FeedbackItem,
  FollowingUser,
} from '@/types/residents/marketplaceListing';
import { MARKETPLACE_API, CACHE_TAGS } from '@/constants/residents/marketplaceConstants';
import {
  ApiResponse,
  CreateReviewRequest,
  BackendFollowingResponse,
  BackendReviewResponse,
} from './types';
import { profileApi } from './profileApi';

// ============================================================================
// Social API
// ============================================================================

export const socialApi = createApi({
  reducerPath: 'marketplaceSocialApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    CACHE_TAGS.Following,
    CACHE_TAGS.Followers,
    CACHE_TAGS.Reviews,
    CACHE_TAGS.SellerProfile,
    CACHE_TAGS.Profile,
  ],
  endpoints: (builder) => ({
    /**
     * Get users that the current user is following
     */
    getFollowing: builder.query<FollowingUser[], void>({
      query: () => ({
        url: MARKETPLACE_API.social.following,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<BackendFollowingResponse[]> | null): FollowingUser[] => {
        if (!response || !response.data) {
          console.warn('getFollowing: Empty or null response from API');
          return [];
        }
        return response.data.map((item) => ({
          id: String(item.userId),
          name: item.name || 'Unknown User',
          initials: item.initials || 'U',
          avatarUrl: item.avatarUrl,
          hasNewListing: item.hasNewListing || false,
          isFollowing: true,
        }));
      },
      providesTags: [{ type: CACHE_TAGS.Following, id: 'LIST' }],
    }),

    /**
     * Get users who follow the current user
     */
    getFollowers: builder.query<FollowingUser[], void>({
      query: () => ({
        url: MARKETPLACE_API.social.followers,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<BackendFollowingResponse[]> | null): FollowingUser[] => {
        if (!response || !response.data) {
          console.warn('getFollowers: Empty or null response from API');
          return [];
        }
        return response.data.map((item) => ({
          id: String(item.userId),
          name: item.name || 'Unknown User',
          initials: item.initials || 'U',
          avatarUrl: item.avatarUrl,
          hasNewListing: false,
          isFollowing: false,
        }));
      },
      providesTags: [{ type: CACHE_TAGS.Followers, id: 'LIST' }],
    }),

    /**
     * Toggle follow/unfollow a user
     */
    toggleFollow: builder.mutation<{ following: boolean }, string>({
      query: (userId) => ({
        url: MARKETPLACE_API.social.follow(userId),
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<{ following: boolean }> | null) => {
        if (!response || !response.data) {
          console.warn('toggleFollow: Empty or null response from API');
          return { following: false };
        }
        return response.data;
      },
      invalidatesTags: (result, error, userId) => [
        { type: CACHE_TAGS.Following, id: 'LIST' },
        { type: CACHE_TAGS.Followers, id: 'LIST' },
        { type: CACHE_TAGS.SellerProfile, id: userId },
        { type: CACHE_TAGS.Profile, id: 'ME' },
      ],
      // SellerProfile and Profile tags live in profileApi, so socialApi's own
      // invalidatesTags can't reach them. Dispatch into profileApi explicitly.
      async onQueryStarted(userId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            profileApi.util.invalidateTags([
              { type: CACHE_TAGS.SellerProfile, id: userId },
              { type: CACHE_TAGS.Profile, id: 'ME' },
            ]),
          );
        } catch {
          // Mutation failed; nothing to invalidate.
        }
      },
    }),

    /**
     * Get reviews for a user
     */
    getReviews: builder.query<FeedbackItem[], { userId?: number }>({
      query: (params) => ({
        url: MARKETPLACE_API.social.reviews,
        method: 'GET',
        params,
      }),
      transformResponse: (response: ApiResponse<BackendReviewResponse[]> | null): FeedbackItem[] => {
        if (!response || !response.data) {
          console.warn('getReviews: Empty or null response from API');
          return [];
        }
        return response.data.map((review) => ({
          id: String(review.id),
          rating: review.rating,
          itemTitle: review.listing?.title || 'Unknown Item',
          itemImageUrl: review.listing?.image,
          reviewText: review.text,
          reviewerName: review.reviewerName || 'Unknown User',
          reviewerInitials: review.reviewerInitials || 'U',
          reviewerAvatarUrl: review.reviewerAvatarUrl,
          date: review.createdAt,
        }));
      },
      providesTags: [{ type: CACHE_TAGS.Reviews, id: 'LIST' }],
    }),

    /**
     * Create a review for another user
     */
    createReview: builder.mutation<void, CreateReviewRequest>({
      query: (data) => ({
        url: MARKETPLACE_API.social.reviews,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: CACHE_TAGS.Reviews, id: 'LIST' },
        { type: CACHE_TAGS.Profile, id: 'ME' },
      ],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetFollowingQuery,
  useLazyGetFollowingQuery,
  useGetFollowersQuery,
  useLazyGetFollowersQuery,
  useToggleFollowMutation,
  useGetReviewsQuery,
  useLazyGetReviewsQuery,
  useCreateReviewMutation,
} = socialApi;

